import type { ReactNode, RefObject } from 'react';

/**
 * Composable application shell scaffold (PRO-0250, DS-6; reskinned PRO-0260 Phase 1;
 * module rail PRO-0340; surface hierarchy PRO-0340 polish). Uses the canvas + inset
 * workspace pattern: `--bg-canvas` app field, `--bg-sidebar` module rail chrome,
 * `--bg-workspace` content column (DS-10). The banner uses the same rail|workspace
 * column grid as the body so the wordmark sits above the module rail and account
 * controls sit above the workspace — not stretched across the full shell width.
 *
 * The shell owns the only `<main>` landmark. Content routes set their own inner
 * width; `main` is programmatically focusable for route-change focus (NF-10). Outer
 * width remains Class 2 Wide (`max-w-shell`) with a single horizontal inset (`px-4`).
 *
 * Sticky footer (not `position: fixed`): the column is `min-h-screen` flex so short
 * pages push `footer` to the bottom of the viewport; long pages scroll normally and
 * the footer follows content (ADR-015 footer values banner).
 */
export interface AppLayoutProps {
  header: ReactNode;
  /** Tier-1 module rail; full-height sidebar chrome on `md+` (PRO-0340). */
  rail?: ReactNode;
  /** Mobile sub-module picker for nested deep modules (ADR-014 A3); omitted on flat modules. */
  subModuleNav?: ReactNode;
  /** Tier-2 task nav; omitted on nested module dashboards (ADR-014 A3). */
  nav?: ReactNode;
  announcer: ReactNode;
  mainRef: RefObject<HTMLElement | null>;
  mainLabel: string;
  children: ReactNode;
  /** Application footer / contentinfo landmark; sits outside `<main>` (unique landmark). */
  footer?: ReactNode;
}

export function AppLayout({
  header,
  rail,
  subModuleNav,
  nav,
  announcer,
  mainRef,
  mainLabel,
  children,
  footer,
}: AppLayoutProps) {
  return (
    <div className="flex min-h-screen flex-col bg-canvas text-text-primary">
      <div className="mx-auto flex w-full max-w-shell flex-1 flex-col px-4">
        <div className="border-b border-border bg-surface">{header}</div>
        <div className="flex flex-1 flex-col md:flex-row">
          {rail}
          <div className="flex min-w-0 flex-1 flex-col bg-workspace shadow-sm">
            {subModuleNav}
            {nav}
            {announcer}
            <main
              ref={mainRef as RefObject<HTMLElement>}
              tabIndex={-1}
              aria-label={mainLabel}
              className="flex-1 px-4 pb-12 pt-6 outline-none"
            >
              {children}
            </main>
          </div>
        </div>
        {footer}
      </div>
    </div>
  );
}
